import React, { useState, useEffect } from 'react';
import { addMedia, updateMedia, deleteMedia } from '../services/mediaService';

// Common media types for quick selection
const MEDIA_TYPES = [
  'Design Disasters',
  'Fried Egg Golf',
  'No Laying Up',
  'Fore Play',
  'Grant Horvat',
  'Bryson DeChambeau',
  'Course Flyover - Jeff Marsh',
  'Golf Digest - Every Hole',
  'Golficity',
  'Gm Golf',
  'Micah Morris',
  'Evan Thompson',
  'Peter Finch Golf',
  'Post on X',
  'Other'
];

function AdminMediaModal({ isOpen, onClose, course, existingMedia, onMediaUpdated }) {
  const [mediaLink, setMediaLink] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [customType, setCustomType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingMedia, setEditingMedia] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setMediaLink('');
      setMediaType('');
      setCustomType('');
      setError('');
      setEditingMedia(null);
    }
  }, [isOpen]);

  // When editing, populate the form
  useEffect(() => {
    if (editingMedia) {
      setMediaLink(editingMedia.media_link || '');
      const type = editingMedia.media_type || '';
      if (MEDIA_TYPES.includes(type)) {
        setMediaType(type);
        setCustomType('');
      } else {
        setMediaType('Other');
        setCustomType(type);
      }
    }
  }, [editingMedia]);

  if (!isOpen || !course) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const finalMediaType = mediaType === 'Other' ? customType : mediaType;

    if (!mediaLink || !finalMediaType) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    try {
      if (editingMedia) {
        await updateMedia(editingMedia.id, {
          mediaLink,
          mediaType: finalMediaType
        });
      } else {
        await addMedia({
          courseId: course.course_id,
          courseName: course.name,
          mediaLink,
          mediaType: finalMediaType
        });
      }

      // Notify parent to refresh media list
      if (onMediaUpdated) {
        onMediaUpdated();
      }

      // Reset form
      setMediaLink('');
      setMediaType('');
      setCustomType('');
      setEditingMedia(null);
      setLoading(false);
    } catch (err) {
      console.error('Error saving media:', err);
      setError('Failed to save media. Please try again.');
      setLoading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media?')) {
      return;
    }

    setLoading(true);
    try {
      await deleteMedia(mediaId);
      if (onMediaUpdated) {
        onMediaUpdated();
      }
      setLoading(false);
    } catch (err) {
      console.error('Error deleting media:', err);
      setError('Failed to delete media. Please try again.');
      setLoading(false);
    }
  };

  const handleEdit = (media) => {
    setEditingMedia(media);
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingMedia(null);
    setMediaLink('');
    setMediaType('');
    setCustomType('');
    setError('');
  };

  return (
    <div className="modal-overlay" style={{
      zIndex: 3000,
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.3)'
    }} onClick={onClose}>
      <div className="modal-content" style={{
        maxWidth: 500,
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
        margin: '40px auto',
        padding: '24px'
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, color: '#1D665D' }}>
            {editingMedia ? 'Edit Media' : 'Add Media'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#666'
            }}
          >
            x
          </button>
        </div>

        {/* Course Info */}
        <div style={{
          background: '#f5f5f5',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 20
        }}>
          <div style={{ fontWeight: 600, color: '#333' }}>{course.name}</div>
          <div style={{ fontSize: 14, color: '#666' }}>
            {course.city}{course.city && course.state ? ', ' : ''}{course.state}
          </div>
        </div>

        {/* Add/Edit Form */}
        <form onSubmit={handleSubmit} style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
              Media Link *
            </label>
            <input
              type="url"
              value={mediaLink}
              onChange={e => setMediaLink(e.target.value)}
              placeholder="https://www.instagram.com/p/... or https://www.youtube.com/..."
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 14,
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
              Media Type *
            </label>
            <select
              value={mediaType}
              onChange={e => setMediaType(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 14,
                boxSizing: 'border-box',
                background: '#fff'
              }}
            >
              <option value="">Select a type...</option>
              {MEDIA_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {mediaType === 'Other' && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#333' }}>
                Custom Type *
              </label>
              <input
                type="text"
                value={customType}
                onChange={e => setCustomType(e.target.value)}
                placeholder="Enter custom media type"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 14,
                  boxSizing: 'border-box'
                }}
              />
            </div>
          )}

          {error && (
            <div style={{ color: '#dc2626', marginBottom: 16, fontSize: 14 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px 20px',
                borderRadius: 8,
                border: 'none',
                background: '#1D665D',
                color: '#fff',
                fontWeight: 600,
                fontSize: 14,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Saving...' : editingMedia ? 'Update Media' : 'Add Media'}
            </button>
            {editingMedia && (
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={loading}
                style={{
                  padding: '12px 20px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  background: '#fff',
                  color: '#666',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        {/* Existing Media List */}
        {existingMedia && existingMedia.length > 0 && (
          <div>
            <h3 style={{ margin: '0 0 12px 0', color: '#333', fontSize: 16 }}>
              Existing Media ({existingMedia.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {existingMedia.map(media => (
                <div
                  key={media.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    background: editingMedia?.id === media.id ? '#e4f8ec' : '#f9f9f9',
                    borderRadius: 8,
                    border: editingMedia?.id === media.id ? '2px solid #1D665D' : '1px solid #eee'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 500,
                      color: '#1D665D',
                      fontSize: 14,
                      marginBottom: 4
                    }}>
                      {media.media_type}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: '#666',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {media.media_link}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginLeft: 12 }}>
                    <button
                      onClick={() => handleEdit(media)}
                      disabled={loading}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: '1px solid #1D665D',
                        background: '#fff',
                        color: '#1D665D',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(media.id)}
                      disabled={loading}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: '1px solid #dc2626',
                        background: '#fff',
                        color: '#dc2626',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminMediaModal;
